/**
 * Typed fetch wrapper for the Moment API.
 * - base path defaults to `/api/v1` (Vite dev proxy forwards it to the FastAPI app)
 * - always sends `credentials: 'include'` so the real panel session cookie is used
 * - optional `getAuthHeaders` adds e.g. `X-Org-Id` in dev or `X-API-Key`
 * - non-2xx responses throw `ApiError { status, detail }`
 */

export interface ApiConfig {
  /** e.g. `/api/v1` or `https://api.example.com/api/v1` */
  baseUrl?: string;
  /** Extra headers per request (dev: `{ 'X-Org-Id': orgId }`). */
  getAuthHeaders?: () => Record<string, string>;
  /** Dev org id. Sent as `X-Org-Id` and used for the `/ws/jobs?org_id=` query param. */
  orgId?: string;
  /** Explicit WebSocket URL for job pushes; derived from `baseUrl` when omitted. */
  wsUrl?: string;
  /** IANA timezone used for date formatting; defaults to the browser timezone. */
  timezone?: string;
}

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown, message?: string) {
    super(message ?? ApiError.describe(status, detail));
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
  static describe(status: number, detail: unknown): string {
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      // FastAPI validation errors
      const parts = detail
        .map((d) => {
          if (d && typeof d === 'object' && 'msg' in d) {
            const loc = Array.isArray((d as { loc?: unknown[] }).loc)
              ? (d as { loc: unknown[] }).loc.filter((x) => x !== 'body').join('.')
              : '';
            return loc ? `${loc}: ${String((d as { msg: unknown }).msg)}` : String((d as { msg: unknown }).msg);
          }
          return JSON.stringify(d);
        })
        .slice(0, 3);
      return parts.join('; ');
    }
    if (detail && typeof detail === 'object' && 'detail' in detail) {
      return ApiError.describe(status, (detail as { detail: unknown }).detail);
    }
    return `Request failed (${status})`;
  }
}

export type QueryValue = string | number | boolean | null | undefined | Array<string | number>;
export type QueryParams = Record<string, QueryValue>;

export function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      for (const item of v) sp.append(k, String(item));
    } else {
      sp.set(k, String(v));
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export interface RequestOptions {
  params?: QueryParams;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export class ApiClient {
  readonly baseUrl: string;
  private readonly config: ApiConfig;

  constructor(config: ApiConfig = {}) {
    this.config = config;
    this.baseUrl = (config.baseUrl ?? '/api/v1').replace(/\/+$/, '');
  }

  get orgId(): string | undefined {
    return this.config.orgId;
  }

  get timezone(): string {
    return this.config.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
  }

  /** Absolute URL for a path under the API base (used for downloads / `window.open`). */
  url(path: string, params?: QueryParams): string {
    return `${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}${buildQuery(params)}`;
  }

  /** ws(s):// URL for `/ws/jobs`, including the dev `org_id` param. */
  wsUrl(): string {
    const params = buildQuery({ org_id: this.config.orgId });
    if (this.config.wsUrl) return `${this.config.wsUrl}${params}`;
    let origin: string;
    let base = this.baseUrl;
    if (/^https?:\/\//i.test(base)) {
      const u = new URL(base);
      origin = u.origin;
      base = u.pathname;
    } else {
      origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    }
    const root = base.replace(/\/api\/v\d+$/i, '').replace(/\/+$/, '');
    const wsOrigin = origin.replace(/^http/i, 'ws');
    return `${wsOrigin}${root}/ws/jobs${params}`;
  }

  headers(extra?: Record<string, string>): Record<string, string> {
    const auth = this.config.getAuthHeaders?.() ?? {};
    const dev: Record<string, string> = this.config.orgId && !auth['X-Org-Id'] ? { 'X-Org-Id': this.config.orgId } : {};
    return { Accept: 'application/json', ...dev, ...auth, ...(extra ?? {}) };
  }

  async request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const headers = this.headers(opts.headers);
    let body: BodyInit | undefined;
    if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
    let res: Response;
    try {
      res = await fetch(this.url(path, opts.params), {
        method,
        headers,
        body,
        credentials: 'include',
        signal: opts.signal,
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      throw new ApiError(0, 'Network error: could not reach the Moment API');
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    let data: unknown = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }
    if (!res.ok) {
      const detail = data && typeof data === 'object' && 'detail' in data ? (data as { detail: unknown }).detail : data;
      throw new ApiError(res.status, detail);
    }
    return data as T;
  }

  get<T>(path: string, params?: QueryParams, signal?: AbortSignal): Promise<T> {
    return this.request<T>('GET', path, { params, signal });
  }
  post<T>(path: string, body?: unknown, params?: QueryParams): Promise<T> {
    return this.request<T>('POST', path, { body, params });
  }
  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', path, { body });
  }
  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, { body });
  }
  delete<T = void>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }

  /**
   * Fetch a binary/redirecting endpoint with auth headers and return a blob URL.
   * Used for `GET /exports/{id}/download` which 307-redirects to a presigned file URL.
   */
  async fetchBlobUrl(path: string): Promise<{ url: string; filename: string | null }> {
    const res = await fetch(this.url(path), { headers: this.headers(), credentials: 'include', redirect: 'follow' });
    if (!res.ok) {
      let detail: unknown = null;
      try {
        detail = (await res.json()) as unknown;
      } catch {
        detail = res.statusText;
      }
      throw new ApiError(res.status, detail);
    }
    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') ?? '';
    // RFC 6266: prefer the UTF-8 `filename*` (Turkish segment names) over the ASCII fallback `filename`.
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(cd);
    const plain = /filename="?([^";]+)"?/i.exec(cd);
    const filename = utf8?.[1] ? decodeURIComponent(utf8[1]) : (plain?.[1] ?? null);
    return { url: URL.createObjectURL(blob), filename };
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

export function errorMessage(e: unknown): string {
  if (isApiError(e)) return e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}
